/**
 * Dashboard View Page
 * Displays a single dashboard with its widgets
 */

import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  RefreshCw,
  Plus,
  Settings,
} from 'lucide-react';
import { Layout } from '../components/layout';
import { DashboardGrid, WidgetEditorModal, WidgetCreateModal } from '../components/dashboard';
import { useDashboard } from '../hooks/useDashboard';
import { dashboardsApi } from '../api/client';
import { Widget } from '../types';

// Layout item type for react-grid-layout
interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export function DashboardViewPage() {
  const { id } = useParams<{ id: string }>();
  const dashboardId = id || null;  // Use UUID string directly
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<LayoutItem[] | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const {
    dashboard,
    isLoading,
    error,
    widgetData,
    widgetLoading,
    widgetErrors,
    refreshWidget,
    refreshAllWidgets,
    refetchDashboard,
  } = useDashboard(dashboardId);

  // Track container width for responsive grid
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleLayoutChange = async (layout: LayoutItem[]) => {
    if (!dashboard || !isEditMode) return;

    // Store layout changes for saving later
    setPendingLayout(layout);
    setHasUnsavedChanges(true);
    console.log('Layout changed:', layout);
  };

  const handleSaveLayout = async () => {
    if (!dashboard) return;

    setIsSaving(true);
    try {
      // Build update data with layout changes
      const updateData: Partial<typeof dashboard> = {
        name: dashboard.name,
        description: dashboard.description,
      };

      // Include layout if there are pending changes
      if (pendingLayout) {
        updateData.layout = pendingLayout;
      }

      // Save dashboard with layout changes
      await dashboardsApi.update(dashboard.id, updateData);

      // Reset state
      setPendingLayout(null);
      setHasUnsavedChanges(false);
      setIsEditMode(false);

      // Refetch dashboard to get updated layout from server
      refetchDashboard();
    } catch (err) {
      console.error('Failed to save layout:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Clear pending changes and refresh to get original layout
    setPendingLayout(null);
    setHasUnsavedChanges(false);
    refreshAllWidgets();
  };

  const handleWidgetEdit = (widget: Widget) => {
    setEditingWidget(widget);
  };

  const handleWidgetDelete = async (widgetId: string) => {
    if (!confirm('Are you sure you want to delete this widget?')) return;

    try {
      // TODO: Delete widget via API
      console.log('Delete widget:', widgetId);
    } catch (err) {
      console.error('Failed to delete widget:', err);
    }
  };

  if (isLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-[#45bdd6] animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !dashboard) {
    return (
      <Layout title="Error">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-red-500 mb-4">
              {error || 'Dashboard not found'}
            </p>
            <Link
              to="/dashboards"
              className="text-[#45bdd6] hover:underline flex items-center gap-2 justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboards
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={dashboard.name}
      onRefresh={refreshAllWidgets}
    >
      <div className="flex flex-col h-full">
        {/* Dashboard Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboards"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {dashboard.name}
              </h1>
              {dashboard.description && (
                <p className="text-sm text-gray-500">{dashboard.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveLayout}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-3 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50"
                >
                  {isSaving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={refreshAllWidgets}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
                <button
                  onClick={() => setIsEditMode(true)}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <Link
                  to={`/dashboards/${dashboard.id}/settings`}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Settings className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Edit Mode Banner */}
        {isEditMode && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-700">
                Edit mode enabled. Drag widgets to rearrange, resize by dragging corners.
              </p>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-[#45bdd6] text-white rounded hover:bg-[#3aa8bf]">
                <Plus className="w-4 h-4" />
                Add Widget
              </button>
            </div>
          </div>
        )}

        {/* Dashboard Grid */}
        <div ref={containerRef} className="flex-1 overflow-auto p-6 bg-gray-50">
          <DashboardGrid
            dashboard={dashboard}
            widgetData={widgetData}
            widgetLoading={widgetLoading}
            widgetErrors={widgetErrors}
            onLayoutChange={handleLayoutChange}
            onWidgetRefresh={refreshWidget}
            onWidgetEdit={handleWidgetEdit}
            onWidgetDelete={handleWidgetDelete}
            onAddWidget={() => setIsCreateModalOpen(true)}
            isEditMode={isEditMode}
          />
        </div>

        {/* Status Bar */}
        <div className="px-6 py-2 border-t border-gray-200 bg-white text-xs text-gray-500 flex items-center justify-between">
          <span>
            {dashboard.widgets.length} widget{dashboard.widgets.length !== 1 ? 's' : ''}
          </span>
          {dashboard.auto_refresh && (
            <span>
              Auto-refresh: {dashboard.refresh_interval_seconds}s
            </span>
          )}
        </div>
      </div>

      {/* Widget Editor Modal */}
      {editingWidget && (
        <WidgetEditorModal
          widget={editingWidget}
          onClose={() => setEditingWidget(null)}
          onSaved={() => {
            refreshAllWidgets();
            setEditingWidget(null);
          }}
        />
      )}

      {/* Widget Create Modal */}
      {isCreateModalOpen && (
        <WidgetCreateModal
          dashboardId={dashboard.id}
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={() => {
            refreshAllWidgets();
            setIsCreateModalOpen(false);
          }}
        />
      )}
    </Layout>
  );
}
